import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Play, Check } from "lucide-react";
import LivingGraph from "@/components/LivingGraph";
import { Reveal, MaskedLines, EASE } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import GraphDiagram from "@/components/diagrams/GraphDiagram";
import CorrelationDiagram from "@/components/diagrams/CorrelationDiagram";
import DriftDiagram from "@/components/diagrams/DriftDiagram";
import SystemDiagram from "@/components/diagrams/SystemDiagram";
import ArchitectureDiagram from "@/components/diagrams/ArchitectureDiagram";

const PROBLEMS = [
  {
    n: "01",
    title: "AI pilots stay isolated",
    body: "Models impress in demos and stall in production. Without shared organizational context, every pilot starts from zero — and stays shallow.",
  },
  {
    n: "02",
    title: "Knowledge is fragmented",
    body: "Critical context lives scattered across tools, teams, and documents. The organization knows far more than any single system can see.",
  },
  {
    n: "03",
    title: "Change happens invisibly",
    body: "Processes drift, ownership shifts, decisions age. By the time the impact surfaces, the cost of correction has already compounded.",
  },
];

const PILLARS = [
  {
    n: "01",
    name: "Context Graph",
    headline: "Build a living map of how your organization actually works.",
    body: "Enterprise knowledge is fragmented across tools, teams, and documents — and AI without context stays shallow. The Context Graph structures the relationships between entities, systems, processes, decisions, and business signals, so intelligence becomes reusable across every initiative.",
    outcome: "Reusable context for every AI system and human operator — no more starting from zero.",
    diagram: <GraphDiagram />,
  },
  {
    n: "02",
    name: "Correlation Engine",
    headline: "Move from isolated signals to meaningful relationships.",
    body: "Enterprises collect signals but struggle to connect them. The Correlation Engine identifies meaningful connections across events, workflows, decisions, and outcomes — surfacing the patterns that matter before they become obvious.",
    outcome: "Disconnected information becomes actionable institutional insight.",
    diagram: <CorrelationDiagram />,
  },
  {
    n: "03",
    name: "Change Observability",
    headline: "See the changes that affect execution before they become expensive blind spots.",
    body: "Institutional knowledge decays when processes, ownership, or business conditions change invisibly. Change Observability makes drift visible — across systems, workflows, and knowledge — so teams detect impact early and respond before decisions go stale.",
    outcome: "Sustained AI performance and organizational alignment, quarter after quarter.",
    diagram: <DriftDiagram />,
  },
];

const WHY = [
  { title: "Faster decisions", body: "Decision history and context arrive with the question — not after a week of meetings and message threads." },
  { title: "Fewer blind spots", body: "Drift in process, knowledge, and systems surfaces while it is still cheap to correct — not after it compounds." },
  { title: "More trustworthy agents", body: "Agents act on shared organizational context, not isolated prompts — and every recommendation stays explainable." },
  { title: "Compounding AI ROI", body: "Every model, copilot, and agent inherits institutional context — each initiative makes the next one cheaper." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGraph = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: yGraph }} className="absolute inset-0">
        <LivingGraph className="h-full w-full" />
      </motion.div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pt-28 sm:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="label-mono"
          data-testid="hero-eyebrow"
        >
          Institutional intelligence for the enterprise
        </motion.p>

        <MaskedLines
          className="mt-8 font-display text-5xl font-medium leading-[1.02] tracking-tighter sm:text-7xl lg:text-8xl"
          lines={["Turn fragmented signals", "into institutional", "intelligence."]}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.8 }}
          className="mt-10 max-w-2xl font-display text-lg font-medium leading-snug text-white/85 sm:text-2xl"
          data-testid="hero-concrete-line"
        >
          Give AI a continuously evolving understanding of your enterprise —
          its context, relationships, decisions and change.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg"
          data-testid="hero-subhead"
        >
          DotIQ helps enterprises capitalize on AI investment — unifying
          organizational context, correlating the signals that matter, and
          making change observable before it becomes a blind spot.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/apply"
            data-testid="hero-apply-cta"
            className="group inline-flex items-center gap-3 bg-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white"
          >
            Apply as Design Partner
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link
            to="/#demo"
            data-testid="hero-demo-cta"
            className="inline-flex items-center gap-3 border border-white/15 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white/70 transition-colors duration-300 hover:border-white/40 hover:text-white"
          >
            See it in action
            <ArrowDown className="h-4 w-4" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-20 flex flex-wrap gap-x-8 gap-y-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/35"
          data-testid="hero-pillar-strip"
        >
          <span>Context Graph</span>
          <span className="text-signal">·</span>
          <span>Correlation Engine</span>
          <span className="text-signal">·</span>
          <span>Change Observability</span>
        </motion.div>
      </motion.div>
    </section>
  );
}

function Problem() {
  return (
    <section className="mx-auto max-w-[1400px] px-6 py-28 sm:px-10 sm:py-40" data-testid="problem-section">
      <Reveal>
        <p className="label-mono">01 — The problem</p>
        <h2 className="mt-6 max-w-3xl font-display text-3xl font-medium tracking-tight sm:text-5xl">
          Enterprises are funding AI. Few are compounding it.
        </h2>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 gap-px bg-white/10 md:grid-cols-3">
        {PROBLEMS.map((p, i) => (
          <Reveal key={p.n} delay={i * 0.12} className="bg-ink p-10 sm:p-12" data-testid={`problem-card-${p.n}`}>
            <span className="font-mono text-xs text-signal">{p.n}</span>
            <h3 className="mt-6 font-display text-xl font-medium tracking-tight sm:text-2xl">{p.title}</h3>
            <p className="mt-4 text-sm leading-relaxed text-white/55">{p.body}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function WhatDotIQDoes() {
  return (
    <section className="border-y border-white/10 bg-surface" data-testid="what-section">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-6 py-24 sm:px-10 sm:py-32 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <p className="label-mono">02 — What DotIQ does</p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-8">
          <div className="border border-white/10 bg-ink p-7 sm:p-9" data-testid="ii-definition">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">Definition</p>
            <p className="mt-4 font-display text-xl font-medium leading-snug tracking-tight sm:text-3xl">
              Institutional Intelligence <span className="text-signal">=</span> the living context
              AI needs to understand how your organization actually works.
            </p>
          </div>
          <p className="mt-10 font-display text-2xl font-medium leading-snug tracking-tight text-white/90 sm:text-4xl" data-testid="what-statement">
            DotIQ builds it as a living, structured system — so every AI
            investment inherits context, every signal finds its relationships,
            and every important change stays visible.
          </p>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/55">
            It does this through three concrete mechanisms: a Context Graph that
            maps organizational entities, systems, decisions, and relationships;
            a Correlation Engine that identifies meaningful connections across
            workflows, signals, and outcomes; and Change Observability that
            makes important change visible before knowledge and decisions drift.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="mt-16">
        <div className="panel overflow-x-auto p-6 sm:p-10" data-testid="architecture-panel">
          <ArchitectureDiagram />
        </div>
      </Reveal>
    </section>
  );
}

function Pillars() {
  return (
    <section id="pillars" className="mx-auto max-w-[1400px] px-6 py-28 sm:px-10 sm:py-40" data-testid="pillars-section">
      <Reveal>
        <p className="label-mono">03 — The platform</p>
        <h2 className="mt-6 max-w-3xl font-display text-3xl font-medium tracking-tight sm:text-5xl">
          Three mechanisms. One institutional memory.
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/55">
          Not three independent tools — one connected system. The Context Graph
          supplies the structure, the Correlation Engine finds the relationships
          within it, and Change Observability keeps both honest — feeding drift
          back into the graph so the system stays current.
        </p>
      </Reveal>

      <Reveal delay={0.1} className="mt-16">
        <div className="panel overflow-x-auto p-6 sm:p-10" data-testid="pillars-system-panel">
          <SystemDiagram />
        </div>
      </Reveal>

      <div className="mt-24 flex flex-col gap-28 sm:gap-36">
        {PILLARS.map((p, i) => (
          <div
            key={p.n}
            className={`grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20 ${i % 2 === 1 ? "" : ""}`}
            data-testid={`pillar-${p.name.toLowerCase().replace(/\s/g, "-")}`}
          >
            <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
              <span className="font-mono text-xs text-signal">{p.n}</span>
              <h3 className="mt-4 font-display text-2xl font-medium tracking-tight sm:text-4xl">{p.name}</h3>
              <p className="mt-6 font-display text-lg leading-snug text-white/85 sm:text-xl">{p.headline}</p>
              <p className="mt-5 text-sm leading-relaxed text-white/55 sm:text-base">{p.body}</p>
              <div className="mt-8 border-l-2 border-signal pl-5">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">Business outcome</p>
                <p className="mt-2 text-sm leading-relaxed text-white/80">{p.outcome}</p>
              </div>
            </Reveal>
            <Reveal delay={0.15} className={i % 2 === 1 ? "lg:order-1" : ""}>
              <div className="panel p-8 sm:p-12">{p.diagram}</div>
            </Reveal>
          </div>
        ))}
      </div>

      <Reveal className="mt-24">
        <Link
          to="/product"
          data-testid="pillars-product-link"
          className="group inline-flex items-center gap-3 border border-white/15 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white/70 transition-colors duration-300 hover:border-signal hover:text-white"
        >
          Go deeper on the product
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>
    </section>
  );
}

function WhyItMatters() {
  return (
    <section id="why" className="border-y border-white/10 bg-surface" data-testid="why-section">
      <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32">
        <Reveal>
          <p className="label-mono">04 — Why it matters</p>
          <h2 className="mt-6 max-w-2xl font-display text-3xl font-medium tracking-tight sm:text-5xl">
            The return on AI is an organizational property.
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/55">
            Institutional intelligence pays out in four concrete ways.
          </p>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08} className="bg-surface p-8" data-testid={`why-card-${i}`}>
              <span className="font-mono text-xs text-white/30">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-5 font-display text-lg font-medium tracking-tight">{w.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/50">{w.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Demo() {
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    setPlaying(true);
    videoRef.current?.play().catch(() => setFailed(true));
  };

  return (
    <section id="demo" className="mx-auto max-w-[1400px] px-6 py-28 sm:px-10 sm:py-40" data-testid="demo-section">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <p className="label-mono">05 — Product demo</p>
          <h2 className="mt-6 font-display text-3xl font-medium tracking-tight sm:text-5xl">See DotIQ in action.</h2>
          <p className="mt-6 text-base leading-relaxed text-white/55">
            Explore how DotIQ uses a Context Graph, a Correlation Engine, and
            Change Observability to turn fragmented organizational signals into
            institutional intelligence.
          </p>
          <Link
            to="/apply"
            data-testid="demo-apply-cta"
            className="group mt-8 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:text-signal"
          >
            Apply as Design Partner
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-8">
          <div className="panel relative aspect-video overflow-hidden" data-testid="demo-video-frame">
            <video
              ref={videoRef}
              className="h-full w-full object-cover"
              poster="/demo-poster.svg"
              controls={playing && !failed}
              playsInline
              preload="none"
              onError={() => setFailed(true)}
              data-testid="demo-video"
            >
              <source src="/demo-v2.webm" type="video/webm" />
              <source src="/demo-v2.mp4" type="video/mp4" />
            </video>
            {!playing && (
              <button
                onClick={handlePlay}
                data-testid="demo-play-button"
                className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors duration-300 hover:bg-black/10"
                aria-label="Play demo"
              >
                <span className="flex h-20 w-20 items-center justify-center rounded-full border border-white/30 bg-black/50 backdrop-blur-md transition-transform duration-300 hover:scale-105">
                  <Play className="ml-1 h-7 w-7 text-white" />
                </span>
              </button>
            )}
            {failed && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-ink/85 p-8 text-center" data-testid="demo-unavailable">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/50">Full walkthrough film</p>
                <p className="max-w-md text-sm leading-relaxed text-white/70">
                  The complete product walkthrough is shared directly with design
                  partner applicants. Apply to receive the private screening link.
                </p>
                <Link to="/apply" className="mt-2 border border-signal px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-signal/10" data-testid="demo-unavailable-cta">
                  Apply for access
                </Link>
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[11px] uppercase tracking-[0.2em] text-white/35">
            <span>00 — Context Graph formation</span>
            <span>01 — Signal correlation</span>
            <span>02 — Drift detection</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DesignPartnerCTA() {
  return (
    <section id="partners" className="border-y border-white/10 bg-surface" data-testid="partners-section">
      <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <p className="label-mono">06 — Design partners</p>
            <h2 className="mt-6 font-display text-3xl font-medium tracking-tight sm:text-5xl">
              Build the institutional intelligence layer with us.
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-white/55">
              We work with a small cohort of enterprises exploring AI maturity —
              CIOs, CTOs, enterprise architecture leaders, and AI transformation
              teams who want their AI agenda grounded in how the organization
              actually works.
            </p>
            <Link
              to="/apply"
              data-testid="partners-apply-cta"
              className="group mt-10 inline-flex items-center gap-3 bg-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white"
            >
              Apply as Design Partner
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:col-span-6">
            {[
              { t: "Who should apply", d: "Medium-to-large enterprises with active AI initiatives and the organizational complexity to match." },
              { t: "Why join now", d: "Shape the product around your environment, and build your intelligence layer before the market catches up." },
              { t: "What it includes", d: "Focused pilots across context mapping, signal correlation, and change observability — with direct access to the founding team." },
              { t: "What it requires", d: "Executive sponsorship, candid feedback, and a willingness to build closely with our team." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08} className="bg-surface p-8" data-testid={`partner-info-${i}`}>
                <h3 className="font-display text-base font-medium tracking-tight">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/50">{c.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Trust() {
  return (
    <section className="mx-auto max-w-[1400px] px-6 py-28 sm:px-10 sm:py-40" data-testid="trust-section">
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <div className="panel max-w-[320px] overflow-hidden">
            <img
              src="/founder.webp"
              alt="Rajni Sharma, Founder of DotIQ"
              className="aspect-square w-full object-cover object-top grayscale"
              data-testid="founder-portrait"
            />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="flex flex-col justify-center lg:col-span-8">
          <p className="label-mono">07 — Founder perspective</p>
          <blockquote className="mt-8 font-display text-2xl font-medium leading-snug tracking-tight text-white/90 sm:text-3xl" data-testid="founder-quote">
            “The next great leap in AI won't come from models knowing more
            about the world. It will come from AI finally understanding the
            organizations it serves — their history, relationships, decisions,
            and collective wisdom. That future is Institutional Intelligence.
            That is why we built DotIQ.”
          </blockquote>
          <a
            href="https://www.linkedin.com/in/rajni-sharma-7850717/"
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/40 transition-colors duration-300 hover:text-white"
            data-testid="founder-linkedin-link"
          >
            Rajni Sharma — Founder, DotIQ
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <div className="mt-12" data-testid="founding-team-strip">
            <p className="label-mono">Founding team</p>
            <div className="mt-5 grid grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
              <a
                href="https://www.linkedin.com/in/rajni-sharma-7850717/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 bg-ink p-5 transition-colors duration-300 hover:bg-elevated"
                data-testid="team-rajni-link"
              >
                <img src="/founder.webp" alt="Rajni Sharma" className="h-11 w-11 rounded-full object-cover grayscale" />
                <span>
                  <span className="block font-display text-sm font-medium">Rajni Sharma</span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Founder</span>
                </span>
                <ArrowUpRight className="ml-auto h-4 w-4 text-white/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white" />
              </a>
              <a
                href="https://www.linkedin.com/in/gautamikella/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 bg-ink p-5 transition-colors duration-300 hover:bg-elevated"
                data-testid="team-gautami-link"
              >
                <img src="/gautami.jpg" alt="Gautami Kella" className="h-11 w-11 rounded-full object-cover grayscale" />
                <span>
                  <span className="block font-display text-sm font-medium">Gautami Kella</span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Founding Engineer</span>
                </span>
                <ArrowUpRight className="ml-auto h-4 w-4 text-white/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white" />
              </a>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-3" data-testid="security-strip">
            {[
              { t: "Your perimeter", d: "Deployed inside your security boundary, under your data-residency rules." },
              { t: "Your data stays yours", d: "Never used to train shared models. Full audit trails from day one." },
              { t: "Enterprise controls", d: "SSO, role-based access, and governance aligned to your policies." },
            ].map((s) => (
              <div key={s.t} className="bg-ink p-6">
                <h4 className="font-display text-sm font-medium">{s.t}</h4>
                <p className="mt-2 text-xs leading-relaxed text-white/50">{s.d}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const inputCls =
  "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-signal";

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      setSent(true);
      toast.success("Message sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="border-t border-white/10 bg-surface" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="label-mono">08 — Contact</p>
            <h2 className="mt-6 font-display text-3xl font-medium tracking-tight sm:text-5xl">
              Talk to us.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/55">
              Questions about the platform, the design partner program, or how
              institutional intelligence fits your AI agenda — send a note and
              the founding team will reply.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            {sent ? (
              <div className="panel flex h-full flex-col items-start justify-center p-10 sm:p-14" data-testid="contact-confirmation">
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-signal">
                  <Check className="h-6 w-6 text-signal" />
                </span>
                <h3 className="mt-8 font-display text-2xl font-medium tracking-tight">Message sent.</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-white/60">
                  Thanks, {form.name.split(" ")[0] || "there"} — your note is on its way to
                  the founding team. Expect a reply within a couple of working days.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="panel p-8 sm:p-10" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/45" htmlFor="c-name">Name</label>
                    <input id="c-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Ada Lovelace" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/45" htmlFor="c-email">Work email</label>
                    <input id="c-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="ada@enterprise.com" data-testid="contact-email-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/45" htmlFor="c-message">Message</label>
                    <textarea id="c-message" rows={4} required value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us what you're exploring." data-testid="contact-message-input" />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="group mt-8 inline-flex items-center gap-3 border border-white/15 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-signal hover:bg-signal/10 disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send message"}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = "DotIQ — Institutional Intelligence";
  }, []);

  return (
    <main>
      <Hero />
      <EditorialMarquee />
      <Problem />
      <WhatDotIQDoes />
      <Pillars />
      <EditorialMarquee />
      <WhyItMatters />
      <Demo />
      <DesignPartnerCTA />
      <Trust />
      <Contact />
    </main>
  );
}
